import React, { useEffect, useRef, useMemo } from 'react';

/**
 * FlickeringGrid Component
 * A flickering grid background made with SVGs, fully customizable
 * Based on MagicUI Flickering Grid
 */
export function FlickeringGrid({
  squareSize = 4,
  gridGap = 6,
  flickerChance = 0.3,
  color = '#10b981',
  width = 1000,
  height = 1000,
  maxOpacity = 0.3,
  className = '',
}) {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);

  const memoizedColor = useMemo(() => {
    // Convert hex to RGB
    const hex = color.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return { r, g, b };
  }, [color]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    canvas.width = width;
    canvas.height = height;

    const cols = Math.floor(width / (squareSize + gridGap));
    const rows = Math.floor(height / (squareSize + gridGap));

    // Initialize grid with random opacities
    const grid = Array.from({ length: rows }, () =>
      Array.from({ length: cols }, () => Math.random() * maxOpacity)
    );

    const drawGrid = () => {
      ctx.clearRect(0, 0, width, height);

      for (let row = 0; row < rows; row++) {
        for (let col = 0; col < cols; col++) {
          const opacity = grid[row][col];
          
          // Randomly flicker
          if (Math.random() < flickerChance) {
            grid[row][col] = Math.random() * maxOpacity;
          }

          ctx.fillStyle = `rgba(${memoizedColor.r}, ${memoizedColor.g}, ${memoizedColor.b}, ${opacity})`;
          ctx.fillRect(
            col * (squareSize + gridGap),
            row * (squareSize + gridGap),
            squareSize,
            squareSize
          );
        }
      }

      animationRef.current = requestAnimationFrame(drawGrid);
    };

    drawGrid();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [squareSize, gridGap, flickerChance, width, height, maxOpacity, memoizedColor]);

  return (
    <canvas
      ref={canvasRef}
      className={className}
      style={{
        width: '100%',
        height: '100%',
      }}
    />
  );
}
